import React, { useState, useEffect } from 'react';
import { X, MapPin, AlertCircle, Loader, Info } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import assetModel from '../../../models/assetModel';

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const AssetDetailModal = ({ asset: initialAsset, onClose }) => {
    const [asset, setAsset] = useState(initialAsset);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        // Fetch full asset details if we only have summary data
        if (initialAsset && initialAsset.PUMP_ID) {
            fetchAssetDetails(initialAsset.PUMP_ID);
        }
    }, [initialAsset]);

    const fetchAssetDetails = async (pumpId) => {
        try {
            setLoading(true);
            setError(null);
            const response = await assetModel.getWaterPump(pumpId);
            
            if (response.success) {
                setAsset(response.data);
            } else {
                setError(response.error || 'Failed to load asset details');
            }
        } catch (err) {
            console.error('Error fetching asset details:', err);
            setError('Failed to load asset details');
        } finally {
            setLoading(false);
        }
    };

    const renderMap = () => {
        if (!asset.has_coordinates || !asset.latitude || !asset.longitude) {
            return (
                <div className="bg-gray-100 rounded-lg p-8 text-center">
                    <AlertCircle className="mx-auto text-gray-400 mb-3" size={48} />
                    <h3 className="font-medium text-gray-700 mb-2">No GPS Coordinates</h3>
                    <p className="text-sm text-gray-500">
                        This asset does not have GPS coordinates available.
                    </p>
                </div>
            );
        }

        const position = [asset.latitude, asset.longitude];

        return (
            <div className="rounded-lg overflow-hidden" style={{ height: '400px' }}>
                <MapContainer
                    center={position}
                    zoom={16}
                    style={{ height: '100%', width: '100%' }}
                >
                    <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <Marker position={position}>
                        <Popup>
                            <div className="p-2">
                                <h3 className="font-semibold text-sm mb-1">{asset.PUMP_NAME}</h3>
                                {asset.STATION_NAME && (
                                    <p className="text-xs text-gray-600">{asset.STATION_NAME}</p>
                                )}
                                <div className="mt-2 text-xs text-gray-500">
                                    <div>Lat: {asset.latitude.toFixed(6)}</div>
                                    <div>Lon: {asset.longitude.toFixed(6)}</div>
                                </div>
                            </div>
                        </Popup>
                    </Marker>
                </MapContainer>
            </div>
        );
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="bg-blue-600 text-white px-6 py-4 flex justify-between items-center">
                    <div className="flex items-center gap-3">
                        <MapPin size={24} />
                        <div>
                            <h2 className="text-xl font-bold">Asset Details</h2>
                            <p className="text-blue-100 text-sm">Water Pump Infrastructure</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-white hover:bg-blue-700 rounded-full p-2 transition"
                    >
                        <X size={24} />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {loading && (
                        <div className="flex items-center justify-center py-12">
                            <Loader className="animate-spin text-blue-600" size={32} />
                            <span className="ml-3 text-gray-600">Loading details...</span>
                        </div>
                    )}

                    {error && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                            <AlertCircle className="text-red-600 flex-shrink-0" size={20} />
                            <div>
                                <h3 className="font-medium text-red-800">Error</h3>
                                <p className="text-red-700 text-sm mt-1">{error}</p>
                            </div>
                        </div>
                    )}

                    {!loading && !error && asset && (
                        <div className="space-y-6">
                            {/* Asset Information */}
                            <div>
                                <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                                    <Info size={18} />
                                    Asset Information
                                </h3>
                                <div className="bg-gray-50 rounded-lg p-4 grid grid-cols-2 gap-4 text-sm">
                                    <div>
                                        <span className="text-gray-600">Pump Name:</span>
                                        <div className="font-medium text-gray-800 mt-1">{asset.PUMP_NAME}</div>
                                    </div>
                                    {asset.STATION_NAME && (
                                        <div>
                                            <span className="text-gray-600">Station:</span>
                                            <div className="font-medium text-gray-800 mt-1">{asset.STATION_NAME}</div>
                                        </div>
                                    )}
                                    {asset.PUMP_MODEL && (
                                        <div>
                                            <span className="text-gray-600">Model:</span>
                                            <div className="font-medium text-gray-800 mt-1">{asset.PUMP_MODEL}</div>
                                        </div>
                                    )}
                                    {asset.RPM && (
                                        <div>
                                            <span className="text-gray-600">RPM:</span>
                                            <div className="font-medium text-gray-800 mt-1">{asset.RPM}</div>
                                        </div>
                                    )}
                                    {asset.TYPE_NAME && (
                                        <div>
                                            <span className="text-gray-600">Type:</span>
                                            <div className="font-medium text-gray-800 mt-1">{asset.TYPE_NAME}</div>
                                        </div>
                                    )}
                                    {asset.SUPPLIER_NAME && (
                                        <div>
                                            <span className="text-gray-600">Supplier:</span>
                                            <div className="font-medium text-gray-800 mt-1">{asset.SUPPLIER_NAME}</div>
                                        </div>
                                    )}
                                    {asset.COMMISSION_DATE && (
                                        <div>
                                            <span className="text-gray-600">Commissioned:</span>
                                            <div className="font-medium text-gray-800 mt-1">
                                                {new Date(asset.COMMISSION_DATE).toLocaleDateString()}
                                            </div>
                                        </div>
                                    )}
                                    {asset.DECOMMISSION_DATE && (
                                        <div>
                                            <span className="text-gray-600">Decommissioned:</span>
                                            <div className="font-medium text-red-600 mt-1">
                                                {new Date(asset.DECOMMISSION_DATE).toLocaleDateString()}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Performance Specs */}
                            {(asset.Q_MIN || asset.Q_MAX || asset.ELEVATION) && (
                                <div>
                                    <h3 className="font-semibold text-gray-800 mb-3">Performance Specifications</h3>
                                    <div className="bg-gray-50 rounded-lg p-4 grid grid-cols-3 gap-4 text-sm">
                                        {asset.Q_MIN && (
                                            <div>
                                                <span className="text-gray-600">Min Flow:</span>
                                                <div className="font-medium text-gray-800 mt-1">{asset.Q_MIN} m³/s</div>
                                            </div>
                                        )}
                                        {asset.Q_MAX && (
                                            <div>
                                                <span className="text-gray-600">Max Flow:</span>
                                                <div className="font-medium text-gray-800 mt-1">{asset.Q_MAX} m³/s</div>
                                            </div>
                                        )}
                                        {asset.ELEVATION && (
                                            <div>
                                                <span className="text-gray-600">Elevation:</span>
                                                <div className="font-medium text-gray-800 mt-1">{asset.ELEVATION} m</div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Location Map */}
                            <div>
                                <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                                    <MapPin size={18} />
                                    GPS Location
                                </h3>
                                {renderMap()}
                                {asset.has_coordinates && (
                                    <div className="mt-3 text-xs text-gray-600 bg-gray-50 rounded p-3">
                                        <div className="grid grid-cols-2 gap-2">
                                            <div>Latitude: <span className="font-mono">{asset.latitude?.toFixed(6)}</span></div>
                                            <div>Longitude: <span className="font-mono">{asset.longitude?.toFixed(6)}</span></div>
                                            {asset.X_COORD && (
                                                <div>X (Raw): <span className="font-mono">{parseFloat(asset.X_COORD).toFixed(2)}</span></div>
                                            )}
                                            {asset.Y_COORD && (
                                                <div>Y (Raw): <span className="font-mono">{parseFloat(asset.Y_COORD).toFixed(2)}</span></div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AssetDetailModal;
